import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getAccessToken, clearAllTokens } from "../userEndApi/tokenStore";
import { logoutApi } from "../userEndApi/AuthApi/LogoutApi";
import apiClient from "../userEndApi/apiClient";

/**
 * AuthContext — provides isLoggedIn, user, login, logout across the frontend.
 * accessToken lives in tokenStore (module variable), never in React state.
 * user data lives here so components can re-render when auth changes.
 */
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [authReady, setAuthReady] = useState(false);

    // On mount — Verify user with backend if token exists
    useEffect(() => {
        const verifyUser = async () => {
            const token = getAccessToken();
            if (!token) {
                setAuthReady(true);
                return;
            }

            try {
                // Try to fetch latest user info from backend
                const response = await apiClient.get("/web-auth/user");
                if (response.data.status === 200 && response.data.data) {
                    const userData = response.data.data;
                    const safe = {
                        id: userData.id,
                        first_name: userData.first_name,
                        last_name: userData.last_name,
                        email: userData.email,
                        profile_picture: userData.profile_picture || null,
                        mobile: userData.mobile || null,
                        address: userData.address || null
                    };
                    setUser(safe);
                    localStorage.setItem("authUser", JSON.stringify(safe));
                } else {
                    throw new Error("Invalid session");
                }
            } catch (error) {
                console.error("Auth verification failed:", error);
                clearAllTokens();
                localStorage.removeItem("authUser");
                setUser(null);
            } finally {
                setAuthReady(true);
            }
        };

        verifyUser();
    }, []);

    /**
     * Fetch latest user data from backend
     * @returns {Promise<Object|null>} User data or null if failed
     */
    const fetchUserData = useCallback(async () => {
        try {
            const response = await apiClient.get("/web-auth/user");
            if (response.data.status === 200 && response.data.data) {
                const userData = response.data.data;
                const safe = {
                    id: userData.id,
                    first_name: userData.first_name,
                    last_name: userData.last_name,
                    email: userData.email,
                    profile_picture: userData.profile_picture || null,
                    mobile: userData.mobile || null,
                    address: userData.address || null
                };
                setUser(safe);
                localStorage.setItem("authUser", JSON.stringify(safe));
                return safe;
            }
            return null;
        } catch (error) {
            console.error("Failed to fetch user data:", error);
            return null;
        }
    }, []);

    /**
     * Call after successful login — stores non-sensitive user data
     * @param {{ id, first_name, last_name, email, profile_picture }} userData
     */
    const login = useCallback((userData) => {
        const safe = {
            id: userData.id,
            first_name: userData.first_name,
            last_name: userData.last_name,
            email: userData.email,
            profile_picture: userData.profile_picture || null,
            mobile: userData.mobile || null,
            address: userData.address || null
        };
        setUser(safe);
        // Store non-sensitive user info so it survives page reload (as fallback)
        localStorage.setItem("authUser", JSON.stringify(safe));
    }, []);

    const logout = useCallback(async () => {
        try {
            await logoutApi();
        } catch (e) {
            console.error("Logout error:", e);
        } finally {
            clearAllTokens();
            localStorage.removeItem("authUser");
            setUser(null);
        }
    }, []);

    return (
        <AuthContext.Provider value={{ user, isLoggedIn: !!user, authReady, login, logout, fetchUserData }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};
